import { JwtPayload } from 'jsonwebtoken'
import { z } from 'zod'
import type { FormFlags } from '@/components/ui/form/form-state'
import { logger } from '@/utils/logger'

export interface PageProps {
  params: { [key: string]: string }
  searchParams: { [key: string]: string | string[] | undefined }
}

export const DateStringSchema = z
  .string()
  .refine(
    (dateString) =>
      !dateString || !Number.isNaN(new Date(dateString).getTime()),
    {
      message: 'Invalid date format',
    }
  )

// =================== COMMON ERROR ====================
export interface ErrorResponse {
  type: string
  title: string
  status: number
  detail: string
  /**
   * Optional Tracking ID to be used for debugging purposes, once an SR is created, this will typically be the SR Number
   */
  trackingId?: string
}

export interface ExtendedErrorResponse extends ErrorResponse {
  coolingPeriod: string
}

export interface StepUpErrorResponse extends ErrorResponse {
  scope?: Array<Permissions>
  amr?: Array<'otp' | 'idcom'>
  acr?: number
}

export function isErrorResponse(
  response: unknown
): response is StepUpErrorResponse {
  if (!response) {
    return false
  }
  return (
    (response as ErrorResponse).status !== undefined &&
    (response as ErrorResponse).title !== undefined
  )
}

// =============== GET SERVICE REQUEST BY SERVICE REQUEST NUMBER ===============
export interface AuditAnalytics {
  os: string
  ip: string
  browser: string
  device: string
  osVersion: string
  browserVersion: string
  sourceType: string
}

export interface DocumentField {
  type: string
}

export interface DocumentItem {
  label: string
  value: DocumentField[]
  selected: boolean
}

export interface DocumentConfig {
  sections: number
  documents: Record<string, DocumentItem>
}

export interface AuditReviewer {
  _id: string
  name?: string
  roles: string[]
  branch: string
  analytics: AuditAnalytics
  sessionId: string
}

export interface CpuData {
  barcode: string
  customerID: string
  accountNumber: string
  statusOfInstruction: string
  holdReturnOkUserID: string
  holdReturnOkDateTime: string
  holdReason1?: string
  holdReason2?: string
  holdReason3?: string
  holdReason4?: string
  holdReason5?: string
  cpuRemarks?: string
  holdDate: string
  holdTillDate: string
  returnedDate: string
  holdReferenceNo: string
}

export interface CPUDto {
  acknowledgementNo: string
  updatedAt: {
    $date: string
  }
  data: CpuData
  occurrence: number
  _class: string
}

export interface AuditEntry {
  event: string
  reviewer: AuditReviewer
  timestamp: {
    $date: string
  }
  actions: string[]
  remark: string
  additionalDetails?: {
    CPU_DTO: (CPUDto | null)[] | null
  }
}

export interface ServiceRequestData {
  id: string
  status: ServiceRequestState2
  journey: Journey2
  data: { [key: string]: string }
  customers: CustomerDetails[]
  applicationDate: string
  remark?: string
  audit?: AuditEntry[]
  instruction?: string
  trackerId?: string
}

/**
 * Enum representing the allowed file action modes for the FileUpload component.
 *
 * Determines which action buttons should be displayed in READ mode:
 *  - SHOW_DOWNLOAD → Only shows the "Download Document" button.
 *  - SHOW_VIEW → Only shows the "View Document" button.
 *  - SHOW_BOTH → Shows both "Download" and "View" buttons.
 *
 * @enum {string}
 * @readonly
 */
export enum FileActionType {
  SHOW_DOWNLOAD = 'download',
  SHOW_VIEW = 'view',
  SHOW_BOTH = 'both',
}

// ===================== CUSTOMER =================================
const ContactsSchema = z.object({
  mobile: z.string(),
  email: z.string().email().nullish(),
})

const KycResponseSchema = z.object({
  aadharCustName: z.string().nullable().optional(),
  aadharDOB: z.string().nullable().optional(),
  aadharGender: z.string().nullable().optional(),
  aadharAddress: z.string().nullable().optional(),
  aadharCity: z.string().nullable().optional(),
  aadharState: z.string().nullable().optional(),
  aadharPincode: z.string().nullable().optional(),
  kycApplicationStatus: z.string().nullable().optional(),
  filler3: z.string().nullable().optional(),
  eKycBioStatus: z.string().nullable().optional(),
  eKycFaceStatus: z.string().nullable().optional(),
  dtKycInitiateDate: z.string().nullable().optional(),
  relMngrMobNo: z.string().nullable().optional(),
  dtRMOtpAuthenticate: z.string().nullable().optional(),
  dtConsentInitiate: z.string().nullable().optional(),
  dtConsentSubmit: z.string().nullable().optional(),
  ekycCompletionDate: z.string().nullable().optional(),
})
const KycSchema = z.object({
  isSignatureAuth: z.boolean().nullable().optional(),
  isBioAuth: z.boolean().nullable().optional(),
  isCustomerChecked: z.boolean().nullable().optional(),
  bioAuthFailedReason: z.string().nullable().optional(),
  codAadhaarNo: z.string().nullable().optional(),
  bioAuthFailedCode: z.string().nullable().optional(),
  latestAuthFail: z.enum(['KYC', 'SIGNATURE']).nullable().optional(),
  kycResponse: KycResponseSchema.nullable().optional(),
  physicalSignatureAuthTimeStamp: z.string().nullable().optional(),
})

// Custom validator for PanNo field
const panNoSchema = z
  .string()
  .refine((val) => /^[A-Z]{5}(\d){4}[A-Z]$/.test(val) || isValidBase64(val), {
    message: 'Invalid PAN format or encrypted string',
  })
  .nullish()

const approvalSchema = z.object({
  approved: z.boolean().optional().default(false),
})

export const IndividualCustomerTypeSchema = z.enum([
  'I',
  'Y',
  'Z',
  'Q',
  'V',
  '2',
  'O',
  '4',
  'M',
  'Y',
  '9',
  'R',
  'N',
  '3',
  'D',
  '8',
  'U',
  '16',
  '5',
  '19',
  '20',
  '21',
  '24',
])

export const NonIndividualCustomerTypeSchema = z.enum([
  'E',
  'J',
  '13',
  '7',
  'F',
  '0',
  'P',
  'K',
  '6',
  'X',
  'T',
  'L',
  'A',
  'S',
  '1',
  'W',
  '11',
  '14',
  '18',
  'H',
  'C',
  'G',
  'B',
  '15',
  '17',
  '12',
  '23',
])

export const CustomerTypeSchema = z.union([
  IndividualCustomerTypeSchema,
  NonIndividualCustomerTypeSchema,
])

export type CustomerType = z.infer<typeof CustomerTypeSchema>

export const AuditAnalyticsSchema = {
  os: z.string(),
  ip: z.string(),
  browser: z.string(),
  device: z.string(),
  osVersion: z.string(),
  browserVersion: z.string(),
  sourceType: z.string(),
}

export const AuditReviewerSchema = z.object({
  _id: z.string(),
  name: z.string().optional(),
  roles: z.array(z.string()),
  branch: z.string(),
  analytics: z.object(AuditAnalyticsSchema),
  sessionId: z.string(),
})

export const CpuDataSchema = z.object({
  barcode: z.string(),
  customerID: z.string(),
  accountNumber: z.string(),
  statusOfInstruction: z.string(),
  holdReturnOkUserID: z.string(),
  holdReturnOkDateTime: z.string(),
  holdReason1: z.string().optional(),
  holdReason2: z.string().optional(),
  holdReason3: z.string().optional(),
  holdReason4: z.string().optional(),
  holdReason5: z.string().optional(),
  cpuRemarks: z.string().optional(),
  holdDate: z.string(),
  holdTillDate: z.string(),
  returnedDate: z.string(),
  holdReferenceNo: z.string(),
})

export const CPUDtoSchema = z.object({
  acknowledgementNo: z.string(),
  updatedAt: z.object({
    $date: z.string(),
  }),
  data: CpuDataSchema,
  occurrence: z.number(),
  _class: z.string(),
})

export const CustomerDetailsAuditSchema = z.object({
  event: z.string(),
  reviewer: AuditReviewerSchema,
  timestamp: z.object({
    $date: z.string(),
  }),
  actions: z.array(z.string()),
  remark: z.string(),
  additionalDetails: z
    .object({
      CPU_DTO: z.array(CPUDtoSchema.nullable()).nullable(),
    })
    .optional(),
})

export const CustomerDetailsSchema = z.object({
  id: z
    .string({ required_error: 'Please Enter Your Customer ID' })
    .min(5, 'Invalid Customer ID')
    .regex(/^[a-zA-Z0-9\s]*$/, {
      message: 'Customer ID Should Be Alphanumeric',
    }),
  name: z.string(),
  gender: z.string().nullish(),
  panNo: panNoSchema,
  dob: z.string().nullish(),
  contacts: ContactsSchema,
  relationship: z.string().nullish(),
  priority: z.number().optional().default(1),
  type: CustomerTypeSchema.nullish().default('I'),
  kycStatus: KycSchema.nullish().optional(),
  approval: approvalSchema.nullish().optional(),
})
export type CustomerDetails = z.infer<typeof CustomerDetailsSchema>

// =============== RAISE & UPDATE SERVICE REQUEST ===============
export interface ServiceRequestCreateUpdateResponse {
  serviceRequestNumber: string
  status: string
  data: {
    [key: string]: FormDataEntryValue
  }
  customers?: CustomerDetails[]
}

// ================ FORM STATE ================

export enum ServiceRequestEvent {
  RM_EDIT,
  RM_REVIEW,
  CUSTOMER_FILL,
  CUSTOMER_EDIT,
  EVALUATING_CUSTOMER_APPROVAL,
  CUSTOMER_APPROVE,
  CUSTOMER_REJECT,
  CUSTOMER_REVIEW,
  CUSTOMER_SEND_BACK,
  BDA_REVIEW,
  BDA_APPROVE,
  BDA_REJECT,
  FTS_APPROVE,
  FTS_REJECTED,
  FTS_HOLD,
  APPROVED,
  CANCELLED,
  TXN_APPROVE,
  TXN_REJECT,
  RM_APPROVE, // new event for locker
  RM_AUTHENTICATE, // new event for locker
  RM_SUBMIT, // new event for locker
  RM_REJECT,
  API_FAILURE,
  PAN_REJECTED,
  SYSTEM_REJECTED,
  CUSTOMER_DELETION_APPROVED,
  ALLOCATE_LOCKER,
  CUSTOMER_ALLOCATION_APPROVED,
  RM_DELETE,
}

export const getStringEvent = (event: ServiceRequestEvent): string => {
  switch (event) {
    case ServiceRequestEvent.RM_EDIT:
      return 'RM_EDIT'
    case ServiceRequestEvent.CUSTOMER_FILL:
      return 'CUSTOMER_EDIT'
    case ServiceRequestEvent.CUSTOMER_EDIT:
      return 'CUSTOMER_EDIT'
    case ServiceRequestEvent.CUSTOMER_REVIEW:
      return 'CUSTOMER_REVIEW'
    case ServiceRequestEvent.RM_REVIEW:
      return 'RM_REVIEW'
    case ServiceRequestEvent.EVALUATING_CUSTOMER_APPROVAL:
      return 'EVALUATING_CUSTOMER_APPROVAL'
    case ServiceRequestEvent.CUSTOMER_APPROVE:
      return 'CUSTOMER_APPROVE'
    case ServiceRequestEvent.CUSTOMER_REJECT:
      return 'CUSTOMER_REJECT'
    case ServiceRequestEvent.CUSTOMER_SEND_BACK:
      return 'CUSTOMER_SEND_BACK'
    case ServiceRequestEvent.BDA_REVIEW:
      return 'BDA_REVIEW'
    case ServiceRequestEvent.BDA_APPROVE:
      return 'BDA_APPROVE'
    case ServiceRequestEvent.BDA_REJECT:
      return 'BDA_REJECT'
    case ServiceRequestEvent.APPROVED:
      return 'CANCELLED'
    case ServiceRequestEvent.CANCELLED:
      return 'CANCELLED'
    case ServiceRequestEvent.RM_AUTHENTICATE: // locker
      return 'RM_AUTHENTICATE'
    case ServiceRequestEvent.RM_SUBMIT: //locker
      return 'RM_SUBMIT'
    case ServiceRequestEvent.RM_APPROVE: //locker
      return 'RM_APPROVE'
    case ServiceRequestEvent.RM_REJECT:
      return 'RM_REJECT'
    case ServiceRequestEvent.API_FAILURE:
      return 'API_FAILURE'
    case ServiceRequestEvent.PAN_REJECTED:
      return 'PAN_REJECTED'
    case ServiceRequestEvent.SYSTEM_REJECTED:
      return 'SYSTEM_REJECTED'
    case ServiceRequestEvent.CUSTOMER_DELETION_APPROVED: //locker WL
      return 'CUSTOMER_DELETION_APPROVED'
    case ServiceRequestEvent.ALLOCATE_LOCKER:
      return 'ALLOCATE_LOCKER'
    case ServiceRequestEvent.CUSTOMER_ALLOCATION_APPROVED:
      return 'CUSTOMER_ALLOCATION_APPROVED'
    case ServiceRequestEvent.RM_DELETE:
      return 'RM_DELETE'
    default:
      return 'UNASSISTED'
  }
}

export const ServiceRequestState2Schema = z.enum([
  'INIT',
  'CUSTOMER_FILL',
  'CUSTOMER_REVIEW',
  'RM_REVIEW',
  'BDA_REVIEW',
  'FTS_PROCESSING',
  'REJECTED',
  'CANCELLED',
  'APPROVED',
  'EVALUATING_CUSTOMER_APPROVAL', // TO-DO: This should not be needed
  'LINK_EXPIRED',
  'ON_HOLD',
  'FTS_REJECTED',
  'TXN_PROCESSING',
  'BAO_PROCESSING',
  'CUSTOMER_AUTHENTICATED',
  'BM_REVIEW', // dormancy removal
  'API_FAILURE',
  'PAN_REJECTED',
  'CPU_PROCESSING',
  'SYSTEM_REJECTED',
  'WAIT_LIST',
  'CUSTOMER_DELETION_REVIEW',
  'REVOKED',
  'SESSION_EXPIRED',
  'CUSTOMER_ALLOCATION_REVIEW',
])

export type ServiceRequestState2 = z.infer<typeof ServiceRequestState2Schema>

export enum ServiceRequestState {
  INIT,
  CUSTOMER_FILL,
  CUSTOMER_REVIEW,
  RM_REVIEW,
  BDA_REVIEW,
  FTS_PROCESSING,
  REJECTED,
  CANCELLED,
  APPROVED,
  EVALUATING_CUSTOMER_APPROVAL,
  CUSTOMER_APPROVE,
  FTS_REJECTED,
  CUSTOMER_AUTHENTICATED,
  API_FAILURE,
  PAN_REJECTED,
  CPU_PROCESSING,
  SYSTEM_REJECTED,
  WAIT_LIST,
  CUSTOMER_DELETION_REVIEW,
  REVOKED,
  CUSTOMER_ALLOCATION_REVIEW,
}

export type ServiceRequestStateType =
  (typeof ServiceRequestState)[keyof typeof ServiceRequestState]

export const Journey2Schema = z.enum(['ASSISTED', 'UNASSISTED'])
export type Journey2 = z.infer<typeof Journey2Schema>
export enum Journey {
  ASSISTED,
  UNASSISTED,
}
export enum FormMode {
  CREATE,
  READ,
  UPDATE,
}

export interface FormResponse {
  status: number
  message: string
  data?: { [key: string]: FormDataEntryValue }
  serviceRequestNumber?: string
  serviceRequestStatus?: string
  maskedCustomers?: CustomerDetails[]
  referenceId?: string
}

export interface SessionData {
  user: User | null
  token: string | null
  refreshToken: string
  sessionId: string | null
  redirectUri?: string
  service_request_number?: string
}

export interface User extends JwtPayload {
  sub: string
  name: string
  auth: string
  roles: Array<string>
  auth_time: number
  exp: number
  branch: { name: string; code: string }
  scope: Array<string>
  ip?: string
  sessionId: string
}

// ================ UAM ===============

// TO-DO: Remove Contact details from API as well
export interface BitlyDetails {
  name: string
  instruction: Instruction
  sessionId: string
  journeyType: Journey2
  type: CustomerTypeBitly
  trackerId?: string
}

export interface Contacts {
  email: string
  mobile: string
}

export interface Instruction {
  name: string
  id: string
}

export const PermissionSchema = z.enum([
  'formSubmit',
  'formFill',
  'formViewSensitive',
  'formReview',
  'formView',
  'lovCreate',
  'lovView',
  'lovUpdate',
  'mappingCreate',
  'mappingView',
  'userCreate',
  'userUpdate',
  'roleCreate',
  'roleUpdate',
  'userApprove',
  'roleApprove',
  'reportsView',
  'reportsDownload',
  'configCreate',
  'configUpdate',
  'configApprove',
  'staff',
  'formSearchAny',
  'superStaff',
])

export type Permissions = z.infer<typeof PermissionSchema>

export interface Customer {
  customerId: string
  name: string
}

export interface CustomerListResponse {
  customerList: Customer[]
}

export type AuthResponse =
  | OtpResponse
  | ErrorResponse
  | IDCOMAuthCodeGenerationResponse
  | CustomerListResponse
  | null

export type Staff = {
  name: string
  branch: {
    name: string
    code: string
  }
}

// =========================== OTP =====================
// Triggered on OTP Generation Response or on OTP Validation Failure where the OTP Entered is Incorrect
export interface OtpResponse {
  generation: AttemptResponse
  validation: AttemptResponse
  contacts: Contacts
}

export function isOtpResponse(response: AuthResponse): response is OtpResponse {
  if (!response) return false
  return (
    (response as OtpResponse).generation !== undefined &&
    (response as OtpResponse).validation !== undefined &&
    (response as OtpResponse).contacts !== undefined
  )
}

export function isRespDataCustomerList(
  respData: AuthResponse
): respData is { customerList: Customer[] } {
  return (
    !!respData &&
    'customerList' in respData &&
    Array.isArray(respData.customerList) &&
    respData.customerList.length > 1
  )
}

export interface AttemptResponse {
  attempted: number
  total: number
}

// ======================= IDCOM ===================
// Triggered on IDCOM Auth Code Generation
export interface IDCOMAuthCodeGenerationResponse {
  redirectUrl: string
  customerId: string
  type: string
  kavachApplicable?: string
}

export function isIDCOMAuthCodeGenerationResponse(
  response: AuthResponse
): response is IDCOMAuthCodeGenerationResponse {
  if (!response) {
    return false
  }
  return (response as IDCOMAuthCodeGenerationResponse).redirectUrl !== undefined
}

export function isEkycIDCOMAuthCodeGenerationResponse(
  response: AuthResponse
): response is IDCOMAuthCodeGenerationResponse {
  if (!response) {
    return false
  }
  const ekycResponse = response as IDCOMAuthCodeGenerationResponse
  return ekycResponse.redirectUrl !== undefined
}

export interface ServiceRequestRaiseRequest {
  schema: string
  event: string
  data?: {
    [key: string]: FormDataEntryValue
  }
  customers: CustomerDetails[]
  journey: Journey2
  flags: Partial<Record<FormFlags, unknown>>
}

export interface ServiceRequestUpdateRequest {
  data?: { [key: string]: FormDataEntryValue }
  remark?: string
  customers?: CustomerDetails[]
  flags?: Partial<Record<FormFlags, unknown>>
}

export interface CommonServiceRequestPayload {
  remark?: string
  schema: string
  event: string
  data?: {
    [key: string]: FormDataEntryValue
  }
  customers?: CustomerDetails[]
  journey: Journey2
  flags: Partial<Record<FormFlags, unknown>>
  trackerId?: string
}

export const CustomerTypeBitlySchema = z.enum(['PRIMARY', 'SECONDARY'])
export type CustomerTypeBitly = z.infer<typeof CustomerTypeBitlySchema>

/**
 * Function will check the input string is valid base64 or not.
 * @param {string} str - The base64 string.
 * @returns {boolean} - return true if input string is base64 otherwise it will return false.
 */
export const isValidBase64 = (str: string): boolean => {
  try {
    return Buffer.from(str, 'base64').toString('base64') === str
  } catch (error) {
    logger.error({ message: 'not a valid base64 string', error })
    return false
  }
}

export interface WaitListDetails {
  isDeleteRequestRaised: boolean
  waitListNumber: string | null
  branchName: string
  branchCode: string
  srNumber: string
  size: string | null
  price: number | null
  lockerStatus: string
  branchAddress: string
}

export interface TrackClaim {
  srNumber: string
  deceasedName: string
  deathCertificateNumber: string
  dateOfRequest: string
  status: string
  user?: User
}
